import { useState, useEffect } from 'react';
import { Star, Eye, EyeOff, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

export default function AdminReviews() {
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseText, setResponseText] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [token]);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateReview = async (reviewId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update review');

      toast.success('Review updated');
      setResponseText('');
      fetchReviews();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div data-testid="admin-reviews">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-semibold text-secondary-900">Reviews</h1>
        <p className="text-muted-foreground">Manage customer reviews and feedback</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="flex justify-center mb-2">
              {[1, 2, 3, 4, 5].map(star => (
                <Star key={star} className={`h-6 w-6 ${star <= Math.round(averageRating) ? 'text-primary fill-primary' : 'text-gray-300'}`} />
              ))}
            </div>
            <p className="text-3xl font-bold text-secondary-900">{averageRating}</p>
            <p className="text-sm text-muted-foreground">Average Rating</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-3xl font-bold text-secondary-900">{reviews.length}</p>
            <p className="text-sm text-muted-foreground">Total Reviews</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-3xl font-bold text-secondary-900">{reviews.filter(r => r.is_visible).length}</p>
            <p className="text-sm text-muted-foreground">Visible Reviews</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map(review => (
            <Card key={review.review_id} className={!review.is_visible ? 'opacity-60' : ''} data-testid={`review-${review.review_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star key={star} className={`h-5 w-5 ${star <= review.rating ? 'text-primary fill-primary' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      {!review.is_visible && (
                        <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">Hidden</span>
                      )}
                    </div>
                    <p className="font-semibold text-secondary-900">{review.customer_name}</p>
                    <p className="text-sm text-muted-foreground mb-2">
                      Order: {review.order_id} • {new Date(review.created_at).toLocaleDateString()}
                    </p>
                    {review.comment && (
                      <p className="text-secondary-800 italic">"{review.comment}"</p>
                    )}
                    {review.admin_response && (
                      <div className="mt-3 pl-4 border-l-2 border-primary bg-muted p-3 rounded">
                        <p className="text-sm font-semibold text-primary">Your Response:</p>
                        <p className="text-sm">{review.admin_response}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateReview(review.review_id, { is_visible: !review.is_visible })}
                      data-testid={`toggle-visibility-${review.review_id}`}
                    >
                      {review.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`respond-${review.review_id}`}>
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Respond
                        </Button>
                      </DialogTrigger>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>Respond to Review</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div className="bg-muted p-4 rounded">
                            <div className="flex mb-2">
                              {[1, 2, 3, 4, 5].map(star => (
                                <Star key={star} className={`h-4 w-4 ${star <= review.rating ? 'text-primary fill-primary' : 'text-gray-300'}`} />
                              ))}
                            </div>
                            <p className="font-semibold">{review.customer_name}</p>
                            <p className="text-sm italic">{review.comment || 'No comment'}</p>
                          </div>
                          <div>
                            <Label>Your Response</Label>
                            <Textarea
                              value={responseText}
                              onChange={(e) => setResponseText(e.target.value)}
                              placeholder="Write your response..."
                              rows={4}
                              data-testid="response-textarea"
                            />
                          </div>
                          <Button
                            onClick={() => updateReview(review.review_id, { admin_response: responseText })}
                            disabled={!responseText.trim()}
                            className="w-full bg-primary hover:bg-primary-600"
                            data-testid="submit-response-btn"
                          >
                            Submit Response
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No reviews yet</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
